import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { IMrhst } from '../repository/mrhst/mrhst.model';
import { PackagegoodsApi } from '../repository/packagegoods/packagegoods.api';
import {
  IPackagegoods,
  PackagegoodsAvailMrhstDto,
} from '../repository/packagegoods/packagegoods.model';
import { StateItem } from '../state-item';
import { Utils } from '../utils';

/**
 * 패키지 상품별 "이용 가능한 시설" 목록 상태.
 *
 * 🔴 **출처는 패키지 상세의 `availHotelMrhstList` 다 — 별도 endpoint 가 아니다.**
 *
 * 이전에는 `GET /api/packagegoods/{id}/availMrhst` 를 호출했으나 **그 endpoint 는 api-1 에도
 * api-2 에도 구현되어 있지 않다.** api-1 은 `ResourceServerConfig:77` 에서 경로를 `permitAll()`
 * 화이트리스트에만 올려놓고 컨트롤러를 만들지 않았다. 즉 호출하면 404 다.
 *
 * CLAUDE.md 도 같은 결론을 이미 적어 두었다 — "이 endpoint 는 패키지에 따라 빈 배열을 반환해
 * 신뢰할 수 없다. `availHotelMrhstList` 를 써라." 실제로는 빈 배열이 아니라 404 였다.
 *
 * 그래서 패키지 상세(`findItem`)를 읽어 거기서 파생시킨다. 백엔드
 * `Packagegoods.availHotelMrhstList: List<MrhstBaseDto>` 가 객체 배열로 직렬화되므로 그것을
 * 우선 쓰고, 일부 레거시 경로에서 JSON 문자열(`availHotelMrhstListJson`)로 오는 경우를 fallback
 * 으로 파싱한다(`booking-create-wizard-dialog.extractAvailHotels()` 와 같은 방식).
 *
 * (2026-08-03 발견·수정 — 그전까지 로더를 부르는 곳조차 없어 목록이 항상 비어 있었다.)
 */
@Injectable()
export class PackagegoodsAvailMrhstStateItem extends StateItem<{
  [id: number]: PackagegoodsAvailMrhstDto;
}> {
  private readonly packagegoodsApi = inject(PackagegoodsApi);

  private currentPackagegoodsId?: number | null;

  constructor() {
    super({});
  }

  /**
   * 패키지의 이용 가능 시설 목록을 적재한다.
   *
   * 같은 패키지는 한 번만 조회하고 이후에는 캐시를 쓴다.
   */
  loadAvailMrhst(packagegoodsId: number | null): void {
    this.currentPackagegoodsId = packagegoodsId;

    if (packagegoodsId == null) {
      return;
    }

    if (this.value[packagegoodsId]) {
      // 이미 적재됨 — 구독자에게 현재 패키지 기준으로 다시 흘려보낸다
      this.setValue({ ...this.value });
      return;
    }

    this.packagegoodsApi.findItem(packagegoodsId).subscribe({
      next: (packagegoods) => {
        this.setValue({
          ...this.value,
          [packagegoodsId]: {
            // 호텔만 객체 배열 필드가 있고, 나머지는 JSON 문자열로만 온다.
            hotelMrhstList: this.extractMrhstList(
              packagegoods?.availHotelMrhstList,
              packagegoods?.availHotelMrhstListJson,
            ),
            attractionMrhstList: this.extractMrhstList(
              undefined,
              packagegoods?.availAttractionMrhstListJson,
            ),
            carMrhstList: this.extractMrhstList(
              undefined,
              packagegoods?.availCarMrhstListJson,
            ),
            guideMrhstList: this.extractMrhstList(
              undefined,
              packagegoods?.availGuideMrhstListJson,
            ),
            restaurantMrhstList: this.extractMrhstList(
              undefined,
              packagegoods?.availRestaurantMrhstListJson,
            ),
            // 패키지 모델에 대응 필드가 없다 — 빈 배열로 둔다(undefined 는 템플릿 @for 를 깬다)
            golfMrhstList: [],
            hotSpringMrhstList: [],
          },
        });
      },
    });
  }

  get hotelMrhstList$(): Observable<IMrhst[]> {
    return this.value$.pipe(
      map((data) => data[this.currentPackagegoodsId!]?.hotelMrhstList ?? []),
    );
  }

  get hotelMrhstList(): IMrhst[] | null {
    return this.value[this.currentPackagegoodsId!]?.hotelMrhstList ?? [];
  }

  /**
   * 객체 배열 우선, JSON 문자열 fallback.
   *
   * 둘 다 없으면 빈 배열이다 — `undefined` 를 흘리면 템플릿 `@for` 가 깨진다.
   */
  private extractMrhstList(
    list: IPackagegoods['availHotelMrhstList'],
    json: string | undefined,
  ): IMrhst[] {
    if (Array.isArray(list) && list.length) {
      return list as unknown as IMrhst[];
    }
    return Utils.getParsedJson(json, []) as IMrhst[];
  }
}
